@file:OptIn(ExperimentalTime::class, ExperimentalUuidApi::class)

package nl.volleyplanner.web

import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

sealed interface BackgroundJobStatus<out R> {
    data object NotStarted : BackgroundJobStatus<Nothing>
    data class Running<R>(
        val startTime: Instant
    ) : BackgroundJobStatus<R> {
        fun toSuccess(endTime: Instant, data: R?) = Success<R>(startTime, endTime, data)
        fun toFailure(endTime: Instant, error: Throwable?) = Failure<R>(startTime, endTime, error)
    }
    data class Success<R>(
        val startTime: Instant,
        val endTime: Instant,
        val result: R?
    ) : BackgroundJobStatus<R>
    data class Failure<R>(
        val startTime: Instant,
        val endTime: Instant,
        val error: Throwable?
    ) : BackgroundJobStatus<R>
}

data class BackgroundJob<T>(
    val jobUuid: Uuid,
    val job: Job,
    val jobStatus: BackgroundJobStatus<T>,
    val state: StateFlow<T?>
) {
    val isRunning: Boolean
        get() = jobStatus is BackgroundJobStatus.Running

    val isCompleted: Boolean
        get() = jobStatus is BackgroundJobStatus.Success<*> || jobStatus is BackgroundJobStatus.Failure
}

private class BackgroundJobHolder(val jobId: String) {
    private val _jobState = MutableStateFlow<BackgroundJob<*>?>(null)
    val jobState = _jobState.asStateFlow()

    fun newJob(backgroundJob: BackgroundJob<*>) {
        require(_jobState.value == null || !_jobState.value!!.job.isActive) { "Cannot create new job for ${jobId}. Already have a running job" }
        _jobState.value = backgroundJob
    }

    @Suppress("UNCHECKED_CAST")
    fun <T> updateState(
        jobUuid: Uuid,
        transform: (BackgroundJob<T>) -> BackgroundJob<T>
    ) {
        _jobState.update { currentState ->
            require(currentState != null)
            require(currentState.jobUuid == jobUuid) { "Cannot update state for job with id $jobUuid. Expected job with id ${currentState.jobUuid}" }

            transform(currentState as BackgroundJob<T>)
        }
    }

    fun clearJob() {
        require(_jobState.value?.job?.isActive != true) { "Cannot clear a running job: $jobId" }
        _jobState.value = null
    }
}

// TODO: Improve so that we can combine the job state with the job data state
//  and avoid observers to require to listen to both
object BackgroundJobs {
    private val jobs = mutableMapOf<String, BackgroundJobHolder>()

    private fun getHolder(jobId: String) : BackgroundJobHolder = jobs.getOrPut(jobId) { BackgroundJobHolder(jobId) }

    fun <T> getJob(jobId: String) : StateFlow<BackgroundJob<T>?> {
        val holder = getHolder(jobId)

        @Suppress("UNCHECKED_CAST")
        return holder.jobState as StateFlow<BackgroundJob<T>?>
    }

    fun <T> launchJob(jobId: String, block: suspend () -> T) : StateFlow<BackgroundJob<T>?> {
        val holder = getHolder(jobId)

        val jobUuid = Uuid.Companion.random()
        val job = CoroutineManager.DISPATCHER_SCOPE.launch(start = CoroutineStart.LAZY) {
            val runningStatus = BackgroundJobStatus.Running<T>(Clock.System.now())
            holder.updateState<T>(jobUuid) { it.copy(jobStatus = runningStatus) }
            val result = runCatching<Any, T> { block() }

            if (result.isSuccess) {
                holder.updateState<T>(jobUuid) {
                    it.copy(jobStatus = runningStatus.toSuccess(Clock.System.now(), result.getOrNull()))
                }
            } else {
                holder.updateState<T>(jobUuid) {
                    it.copy(jobStatus = runningStatus.toFailure(Clock.System.now(), result.exceptionOrNull()))
                }
            }
        }
        holder.newJob(BackgroundJob<T>(jobUuid, job, BackgroundJobStatus.NotStarted, MutableStateFlow<T?>(null)))
        job.start()
        @Suppress("UNCHECKED_CAST")
        return holder.jobState as StateFlow<BackgroundJob<T>?>
    }

    /**
     * Quick and dirty way to remove job status so it is no longer displayed to the user
     * Longer term need to subscribe observer on the job to enable it to know it has seen the event, so multiple
     * observers can work with this
     */
    fun clearJob(jobId: String) {
        getHolder(jobId).clearJob()
    }
}